import { DashboardIcon } from "@/assets/images/sidebar-icons/dashboard"
import { PaymentIcon } from "@/assets/images/sidebar-icons/payment"
import { MessagesIcon } from "@/assets/images/svgIcons/messagesIcon"
import type { MenuItem } from "@/components/ui/layout/menuSidebar/type-sidebar"
import { getCoachPath } from "@/modules/navigation/hooks/useCoachNavigate"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { BoxIcon, CalendarDays, Dumbbell, DotIcon, SettingsIcon, UserRound } from "lucide-react"

export type CoachMenuConfig = {
  coachId: string
  hasUnreadMessage: boolean
}

export const createCoachMenuModel = (config: CoachMenuConfig): MenuItem[] => {
  const { hasUnreadMessage } = config
  const menu: MenuItem[] = []

  const itemDashboard: MenuItem = {
    label: word("menu.dashboard"),
    icon: <DashboardIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getCoachPath(Path.Coach.Dashboard, { newCoachId: config.coachId }),
    relativeTo: [getCoachPath(Path.Coach.Dashboard, { newCoachId: config.coachId })],
  }

  const itemPlanning: MenuItem = {
    label: word("menu.planning"),
    icon: <CalendarDays className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getCoachPath(Path.Coach.Planning, { newCoachId: config.coachId }),
    relativeTo: [getCoachPath(Path.Coach.Planning, { newCoachId: config.coachId })],
  }

  const itemMembers: MenuItem = {
    label: word("menu.members"),
    icon: <UserRound className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getCoachPath(Path.Coach.Members, { newCoachId: config.coachId }),
    relativeTo: [getCoachPath(Path.Coach.Members, { newCoachId: config.coachId })],
  }

  const itemProgrammation: MenuItem = {
    label: word("menu.programmation.detail"),
    icon: <Dumbbell className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getCoachPath(Path.Coach.Programmation, { newCoachId: config.coachId }),
    relativeTo: [getCoachPath(Path.Coach.Programmation, { newCoachId: config.coachId })],
  }

  const itemOffers: MenuItem = {
    label: word("menu.offers"),
    icon: <BoxIcon className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getCoachPath(Path.Coach.Offers, { newCoachId: config.coachId }),
    relativeTo: [getCoachPath(Path.Coach.Offers, { newCoachId: config.coachId })],
  }

  const itemChat: MenuItem = {
    label: word("menu.chat"),
    icon: (
      <span className="relative inline-block">
        <MessagesIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />
        {hasUnreadMessage && (
          <span className="absolute -top-0.5 right-0 flex h-2 w-2 items-center justify-center">
            <DotIcon strokeWidth={20} color="currentColor" className="text-red-500" />
          </span>
        )}
      </span>
    ),
    to: getCoachPath(Path.Chat.Root, { newCoachId: config.coachId }),
    badge: hasUnreadMessage ? ("new" as "new" | "updated") : undefined,
    relativeTo: [getCoachPath(Path.Chat.Root, { newCoachId: config.coachId })],
  }

  const itemFinances: MenuItem = {
    label: word("menu.finances"),
    icon: <PaymentIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getCoachPath(Path.Coach.Finances, { newCoachId: config.coachId }),
    relativeTo: [getCoachPath(Path.Coach.Finances, { newCoachId: config.coachId })],
  }

  const itemAdmin: MenuItem = {
    label: word("menu.admin"),
    icon: <SettingsIcon className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getCoachPath(Path.Coach.Admin, { newCoachId: config.coachId }),
    relativeTo: [getCoachPath(Path.Coach.Admin, { newCoachId: config.coachId })],
  }

  menu.push(itemDashboard)
  menu.push(itemPlanning)
  menu.push(itemMembers)
  menu.push(itemProgrammation)
  menu.push(itemOffers)
  menu.push(itemChat)
  menu.push(itemFinances)
  menu.push(itemAdmin)

  return menu
}
